import React, { useState } from 'react';
import { Download, X, FileText, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable in-place document viewer.
 *
 * Usage:
 *   const viewer = useDocumentViewer();
 *   <Button onClick={() => viewer.open({
 *     url: '/api/admin/recruiters/REC-2026-1234/documents/business_license',
 *     label: 'Business License',
 *     ownerName: 'Pinnacle Recruiters',
 *   })}>...</Button>
 *   {viewer.element}
 *
 * The hook handles fetching with credentials, blob conversion, error mapping,
 * inline preview (PDF iframe / image / fallback), and download.
 */
export function useDocumentViewer() {
  const [state, setState] = useState(null); // { url, label, ownerName, blobUrl, mime, filename, error, loading }

  const close = () => {
    setState((prev) => {
      if (prev?.blobUrl) URL.revokeObjectURL(prev.blobUrl);
      return null;
    });
  };

  const open = async ({ url, label, ownerName }) => {
    const fullUrl = url.startsWith('http') ? url : `${API}${url}`;
    setState({ url: fullUrl, label, ownerName, loading: true });
    try {
      const res = await fetch(fullUrl, { credentials: 'include' });
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.json()).detail; } catch (_) {}
        const map = {
          401: 'Your session has expired — please log in again.',
          403: "You don't have permission to view this document.",
          404: detail === 'Document not uploaded' || detail === 'Document not found on profile'
            ? 'This document was never uploaded.'
            : 'The document download endpoint is not available. If this is production, please redeploy so the latest backend changes take effect.',
          410: 'The file no longer exists on disk. It may have been removed during a deployment that did not preserve uploads.',
        };
        setState({ url: fullUrl, label, ownerName, error: map[res.status] || detail || `Failed (HTTP ${res.status})` });
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const mime = blob.type || res.headers.get('content-type') || 'application/octet-stream';
      const filename = (res.headers.get('content-disposition') || '').match(/filename="?([^"]+)"?/)?.[1];
      setState({ url: fullUrl, label, ownerName, blobUrl, mime, filename });
    } catch (err) {
      setState({ url: fullUrl, label, ownerName, error: err.message || 'Network error' });
    }
  };

  const downloadCurrent = () => {
    if (!state?.blobUrl) return;
    const a = document.createElement('a');
    a.href = state.blobUrl;
    a.download = state.filename || `${(state.ownerName || 'document').replace(/[^\w-]+/g, '-')}-${(state.label || 'file').toLowerCase().replace(/\s+/g, '-')}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const element = (
    <Dialog open={!!state} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent
        className="max-w-5xl w-[92vw] h-[88vh] p-0 gap-0 overflow-hidden flex flex-col"
        data-testid="doc-viewer-dialog"
      >
        <div className="px-5 py-3 border-b border-tge-navy/10 bg-tge-cream-soft flex items-center gap-3 shrink-0">
          <FileText size={16} className="text-tge-navy/60" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-tge-navy capitalize truncate">{state?.label}</div>
            <div className="text-[11px] text-tge-navy/50 truncate">
              {state?.ownerName ? `${state.ownerName} · ` : ''}{state?.filename || state?.mime || ''}
            </div>
          </div>
          {state?.blobUrl && (
            <Button
              size="sm"
              onClick={downloadCurrent}
              className="bg-tge-navy hover:bg-tge-navy-deep text-white gap-1.5 h-8"
              data-testid="doc-viewer-download-btn"
            >
              <Download size={13} /> Download
            </Button>
          )}
          <button
            onClick={close}
            className="w-8 h-8 rounded-lg hover:bg-tge-navy/10 flex items-center justify-center text-tge-navy/60 transition"
            aria-label="Close"
            data-testid="doc-viewer-close-btn"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-hidden bg-slate-100">
          {state?.error ? (
            <div className="h-full flex flex-col items-center justify-center px-8 py-12 text-center gap-3">
              <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
                <AlertCircle size={22} />
              </div>
              <div className="text-tge-navy font-semibold">Couldn't load this document</div>
              <div className="text-sm text-tge-navy/70 max-w-md">{state.error}</div>
            </div>
          ) : state?.loading ? (
            <div className="h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-tge-navy" />
            </div>
          ) : state?.blobUrl ? (
            state.mime?.startsWith('image/') ? (
              <div className="h-full overflow-auto flex items-center justify-center p-4">
                <img
                  src={state.blobUrl}
                  alt={state.label}
                  className="max-w-full max-h-full object-contain shadow-lg"
                  data-testid="doc-viewer-image"
                />
              </div>
            ) : (state.mime?.includes('pdf') || (state.filename || '').toLowerCase().endsWith('.pdf')) ? (
              <iframe
                src={state.blobUrl}
                title={state.label}
                className="w-full h-full border-0"
                data-testid="doc-viewer-pdf"
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center px-8 py-12 text-center gap-3">
                <FileText size={36} className="text-tge-navy/40" />
                <div className="text-tge-navy font-semibold">Preview not supported for this file type</div>
                <div className="text-sm text-tge-navy/60">{state.mime || 'Unknown type'} — please download to view.</div>
                <Button onClick={downloadCurrent} className="bg-tge-navy hover:bg-tge-navy-deep text-white mt-2 gap-1.5">
                  <Download size={14} /> Download file
                </Button>
              </div>
            )
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );

  return { open, close, element, state };
}
